import { useEffect, useRef, useState } from 'react';
import { getFileDownloadUrl } from '@/api/file';

/**
 * Resolves stored file ids to displayable URLs.
 *
 * Columns like `Field.photoFileId` hold an id, not a URL — dropping the id into
 * `<img src>` renders a broken image, which is exactly what the thửa-đất cards
 * did. Each id is fetched once per mounted component; a failed resolve is
 * retried on the next render pass rather than cached as a permanent blank.
 */
export function useFileUrls(fileIds: (string | undefined | null)[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const requested = useRef<Set<string>>(new Set());
  // A stable string key: the array identity changes on every render, the ids don't.
  const key = fileIds.filter((id): id is string => Boolean(id)).join(',');

  // Unmount, not "this effect run ended", is what makes a result unwanted. A
  // per-run cancel flag dropped every resolve that was still in flight when the
  // id list changed (or when StrictMode re-ran the effect), while the id stayed
  // marked as requested — so that file was never asked for again and its
  // thumbnail spun forever.
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const ids = key ? key.split(',') : [];
    for (const id of ids) {
      if (requested.current.has(id)) continue;
      requested.current.add(id);
      getFileDownloadUrl(id)
        .then((url) => {
          if (mounted.current) setUrls((prev) => (prev[id] ? prev : { ...prev, [id]: url }));
        })
        .catch(() => {
          requested.current.delete(id);
        });
    }
  }, [key]);

  return urls;
}
